/**
 * Геокодер Яндекса (HTTP API 1.x) — прямо из браузера, ключом из
 * `MapKeySource` (тот же, что у карты: ключ открыт для обоих API и ограничен
 * доменами приложений). Общий для обоих приложений: покупатель ищет адрес
 * доставки, админка — точку аптеки. Это чужой сервис, а не наш API, — отсюда
 * свой `fetch`, а не клиент `@dorixona/api`.
 */
import type { MapKeySource } from './index'

export const GEOCODER_URL = 'https://geocode-maps.yandex.ru/1.x/'

/** Ташкент с пригородами: поиск не уводит в одноимённые улицы других городов. */
const TASHKENT_BBOX = '69.05,41.15~69.50,41.45'

/** Найденное место: подпись (улица, дом) и точка. */
export interface GeoAddress {
  label: string
  /** Где это: город, район — вторая строка в списке найденного. */
  note?: string
  lat: number
  lng: number
}

interface GeoObject {
  name?: string
  description?: string
  Point?: { pos?: string }
}

interface GeocoderAnswer {
  response?: {
    GeoObjectCollection?: { featureMember?: { GeoObject?: GeoObject }[] }
  }
}

/** Язык ответа: улицы подписаны так, как читает человек. */
export function geocoderLang(language: string): string {
  return language.startsWith('uz') ? 'uz_UZ' : 'ru_RU'
}

/** `pos` у Яндекса — «долгота широта» через пробел. */
function toAddress(object: GeoObject): GeoAddress | null {
  const [lng, lat] = (object.Point?.pos ?? '').split(' ').map(Number)
  if (!object.name || !lat || !lng) return null
  return object.description
    ? { label: object.name, note: object.description, lat, lng }
    : { label: object.name, lat, lng }
}

/** Так Яндекс отказывает ключу: неверный (403) или дневной лимит кончился (429). */
const REFUSED = new Set([403, 429])

function request(apiKey: string, params: Record<string, string>): Promise<Response> {
  const query = new URLSearchParams({ format: 'json', apikey: apiKey, ...params })
  return fetch(`${GEOCODER_URL}?${query.toString()}`)
}

/** Ключу отказали — источник даёт другой, пробуем им один раз. */
async function geocode(keys: MapKeySource, params: Record<string, string>): Promise<GeoAddress[]> {
  const apiKey = await keys.key()
  // Ключа нет (пул пуст) — Яндекс всё равно откажет, не спрашиваем.
  if (!apiKey) throw new Error('Yandex Geocoder key is empty')
  let response = await request(apiKey, params)
  if (REFUSED.has(response.status)) {
    const next = await keys.refused(apiKey)
    if (next && next !== apiKey) response = await request(next, params)
  }
  if (!response.ok) throw new Error(`Yandex Geocoder answered ${response.status}`)
  const answer = (await response.json()) as GeocoderAnswer
  return (answer.response?.GeoObjectCollection?.featureMember ?? []).flatMap((member) => {
    const address = member.GeoObject ? toAddress(member.GeoObject) : null
    return address ? [address] : []
  })
}

/** Улица и дом у точки на карте: ближайший дом, а не район или город. */
export async function reverseGeocode(
  keys: MapKeySource,
  point: { lat: number; lng: number },
  lang: string,
): Promise<string> {
  const [found] = await geocode(keys, {
    geocode: `${point.lng},${point.lat}`,
    kind: 'house',
    results: '1',
    lang,
  })
  return found?.label ?? ''
}

/** Адреса по набранному тексту — только в Ташкенте. */
export function searchAddresses(
  keys: MapKeySource,
  query: string,
  lang: string,
): Promise<GeoAddress[]> {
  return geocode(keys, {
    geocode: query,
    bbox: TASHKENT_BBOX,
    rspn: '1',
    results: '5',
    lang,
  })
}
